import { useState, useEffect } from 'react';
import styles from './bulkUploadImagesModal.module.scss';
import { IoMdClose } from 'react-icons/io';
import ButtonShared from '../Button';
import { FiUpload } from 'react-icons/fi';
import { FiFile, FiX } from 'react-icons/fi';
import ImportSummaryModal from '../ImportSummaryModal/ImportSummaryModal';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import { BulkUploadImagesModalProps } from '../../interfaces/components';
import { useNavigate } from 'react-router-dom';

const BulkUploadImagesModal = ({
  isOpen,
  onClose,
  vehicleType,
  selectedPublicationMethod,
  onUploadComplete,
  onCloseAll,
  massiveCharge,
  bulkUploadImages,
  bulkUploadImagesStorage,
  validateUploadStatus,
  cleanMassiveCharge,
  confirmUploadProcess
}: BulkUploadImagesModalProps) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string>('');
  const [lineCount, setLineCount] = useState<number | null>(null);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [archiveId, setArchiveId] = useState<string | null>(null);
  const [photoReplaceOption, setPhotoReplaceOption] = useState<'replace' | 'keep'>('replace');
  const [isDragging, setIsDragging] = useState(false);
  const [replacement, setReplacement] = useState(false);
  const navigate = useNavigate()


  useEffect(() => {
    if (massiveCharge?.uploadResult?.url && selectedFile) {
      bulkUploadImagesStorage(selectedFile, massiveCharge.uploadResult?.url);
    }
  }, [massiveCharge?.uploadResult?.url, selectedFile]);

  useEffect(() => {
    if (isOpen && massiveCharge.uploadResult?.data?.process_publication?.processPublication?.id) {
      const extractedArchiveId = massiveCharge.uploadResult.data.process_publication.processPublication.id;
      setArchiveId(extractedArchiveId);
    }
  }, [isOpen, massiveCharge.uploadResult]);
  
  useEffect(() => {
    if (massiveCharge.uploadStorageResult?.fetching === "success" && archiveId) {      
      if (archiveId) {
        confirmUploadProcess(archiveId, massiveCharge.uploadResult?.name, replacement);
      } else {
        console.error('Missing required data for confirmation');
      }
    }
  }, [massiveCharge.uploadStorageResult?.fetching, archiveId, replacement, confirmUploadProcess]);

  const summaryData = {
    totalCount: lineCount ? Math.min(lineCount - 1, 25) : 25,
    identifiedCount: lineCount ? Math.floor((lineCount - 1) * 0.8) : 25,
    incompleteCount: lineCount ? Math.floor((lineCount - 1) * 0.15) : 0,
    notImportedCount: lineCount ? Math.floor((lineCount - 1) * 0.05) : 0,
    errorFileName: 'listado-no-reconocidos.xlsx'
  };

  const resetInternalState = () => {
    setSelectedFile(null);
    setFileError('');
    setShowSummaryModal(false);
    setArchiveId(null);
    setPhotoReplaceOption('replace');

    const fileInput = document.getElementById('images-file-upload') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleClose = () => {
    resetInternalState();
    onClose();
  };

  const handleCloseAll = () => {
    resetInternalState();
    onCloseAll();
  };

  if (!isOpen) return null;

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB en bytes

    if (files && files.length > 0) {
      const file = files[0];

      if (!file.name.endsWith('.zip')) {
        setFileError('Por favor sube un archivo .zip con las imágenes');
        setSelectedFile(null);
        return;
      }
      
      if (file.size > MAX_FILE_SIZE) {
        setFileError(`El archivo es demasiado grande. El tamaño máximo permitido es 100MB. Tu archivo tiene ${(file.size / (1024 * 1024)).toFixed(2)}MB`);
        setSelectedFile(null);
        return;
      }

      setSelectedFile(file);
      setFileError('');
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    const fileInput = document.getElementById('images-file-upload') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  };

  // Drag and drop handlers
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set isDragging to false if we're leaving the uploadBox itself
    if (e.currentTarget.contains(e.relatedTarget as Node)) {
      return;
    }
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB en bytes

    if (files && files.length > 0) {
      const file = files[0];

      if (!file.name.endsWith('.zip')) {
        setFileError('Por favor sube un archivo .zip con las imágenes');
        setSelectedFile(null);
        return;
      }
      
      if (file.size > MAX_FILE_SIZE) {
        setFileError(`El archivo es demasiado grande. El tamaño máximo permitido es 100MB. Tu archivo tiene ${(file.size / (1024 * 1024)).toFixed(2)}MB`);
        setSelectedFile(null);
        return;
      }

      setSelectedFile(file);
      setFileError('');
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      console.log('❌ No file selected');
      return;
    }

    if (!archiveId) {
      console.log('❌ No archiveId available');
      return;
    }

    try {
      const result = await bulkUploadImages(selectedFile, vehicleType, archiveId, replacement);
      setShowSummaryModal(true);

    } catch (error) {
      console.error('❌ Error uploading images file:', error);
    }
  };

  const handleSummaryModalClose = () => {
    // Close the modal and keep the selected file for retry
    setShowSummaryModal(false);
  };

  const handleCompleteImport = () => {
    setShowSummaryModal(false);
    onCloseAll();
    resetInternalState();
    if(massiveCharge?.confirmStorage?.success){
      cleanMassiveCharge();
      navigate('/profile/my_publications')
      window.location.reload();
    }

    if (selectedFile && onUploadComplete) {
      onUploadComplete(selectedFile);
    }
  };


  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContainer}>
        <button className={styles.closeButton} onClick={handleCloseAll}>
          <IoMdClose />
        </button>

        <div className={styles.modalHeader}>
          <h2>Sube las fotos de tu autos</h2>
        </div>

        <div className={styles.modalContent}>
          <p className={styles.instructions}>
            Carga un archivo .zip con las fotos de los autos del listado anterior. Dentro del .zip, usa esta nomenclatura:
          </p>
          <div className={styles.nomenclatureContainer}>
            <div className={styles.nomenclatureItem}>
              <span className={styles.nomenclatureBullet}>•</span>
              <span className={styles.nomenclatureFilename}><strong>patente</strong>.jpg</span>
              <ArrowRightAltIcon className={styles.nomenclatureArrow} />
              <span className={styles.nomenclatureDescription}>foto principal (portada)</span>
            </div>
            <div className={styles.nomenclatureItem}>
              <span className={styles.nomenclatureBullet}>•</span>
              <span className={styles.nomenclatureFilename}><strong>patente-1</strong>.jpg, <strong>patente-2</strong>.jpg, ...</span>
              <ArrowRightAltIcon className={styles.nomenclatureArrow} />
              <span className={styles.nomenclatureDescription}>fotos adicionales del mismo auto</span>
            </div>
          </div>

          <div className={styles.exampleText}>
            Ejemplo: ABCD23.jpg, ABCD23-1.jpg, ABCD23-2.jpg.
          </div>

          {!selectedFile ? (
            <div
              className={`${styles.uploadBox} ${isDragging ? styles.uploadBoxDragging : ''}`}
              onDragOver={handleDragOver}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <div className={styles.uploadContent}>
                <input
                  type="file"
                  id="images-file-upload"
                  accept=".zip"
                  onChange={handleFileChange}
                  className={styles.fileInput}
                />
                <label htmlFor="images-file-upload" className={styles.uploadLabel}>
                  <FiUpload size={24} />
                  <span>Subir archivo o arrastra aquí</span>
                </label>
                <p className={styles.formatText}>
                  Formato admitido: .zip (máximo 100MB)
                </p>
              </div>
            </div>
          ) : (
            <div className={styles.selectedFileContainer}>
              <div className={styles.selectedFileContent}>
                <div className={styles.fileIconContainer}>
                  <FiFile size={20} />
                </div>
                <span className={styles.fileName}>{selectedFile.name}</span>
                <button
                  className={styles.removeFileButton}
                  onClick={handleRemoveFile}
                  aria-label="Eliminar archivo"
                >
                  <FiX size={18} />
                </button>
              </div>
            </div>
          )}

          {fileError && (
            <div className={styles.errorMessage}>
              <p>{fileError}</p>
            </div>
          )}
        </div>

        <div className={styles.replacementContainer}>
          <div className={styles.radioGroup}>
            <p className={styles.radioTitle}>Indica qué hacer si ya subiste fotos con la misma nomenclatura:</p>
            <label className={styles.radioLabel}>
              <input
                type="radio"
                name="replacementImgOption"
                value="new"
                checked={replacement}
                onChange={() => setReplacement(true)}
              />
              <span>Reemplazar fotos</span>
            </label>
            <label className={styles.radioLabel}>
              <input
                type="radio"
                name="replacementImgOption"
                value="replace"
                checked={!replacement}
                onChange={() => setReplacement(false)}
              />
              <span>Mantener las existentes y sumar las nuevas</span>
            </label>
          </div>
        </div>


        <div className={styles.modalFooter}>
          <ButtonShared
            onPress={handleClose}
            title="Volver"
            tertiary
            className={styles.cancelButton}
            id="cancelBulkUploadImagesButton"
            size={"small"}
          />
          <ButtonShared
            onPress={handleUpload}
            title={massiveCharge.fetching === 'loading' ? "Cargando..." : "Publicar autos"}
            primary
            disabled={!selectedFile || massiveCharge.fetching === 'loading'}
            className={styles.continueButton}
            size={"small"}
            loading={massiveCharge.fetching === 'loading'}
          />

        </div>
      </div>

      <ImportSummaryModal
        isOpen={showSummaryModal}
        onClose={handleSummaryModalClose}
        onComplete={handleCompleteImport}
        vehicleType={vehicleType}
        totalCount={summaryData.totalCount}
        identifiedCount={summaryData.identifiedCount}
        incompleteCount={summaryData.incompleteCount}
        notImportedCount={summaryData.notImportedCount}
        fileName={summaryData.errorFileName}
        archiveId={archiveId || undefined}
        massiveCharge={massiveCharge}
        validateUploadStatus={validateUploadStatus}
      />
    </div>
  );
};

export default BulkUploadImagesModal;
